import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getAllShop } from '@/api/shop';
import { Storage } from '@/utils/storage/storage';

/** 档口项 */
export interface StallItem {
  id: number
  shopName: string
}

/** 单个站点的缓存数据 */
interface StallCacheEntry {
  list: StallItem[]
  loadedAt: number
}

/** 缓存键名前缀 */
const CACHE_PREFIX = 'STALL_LIST_';
/** 缓存有效期：30分钟（秒） */
const CACHE_EXPIRE = 60 * 60 * 6;

export const useStallListStore = defineStore('stallList', () => {
  // ====== State ======
  /** 内存缓存：key = siteCode */
  const cache = ref<Record<string, StallCacheEntry>>({});
  /** 加载状态：key = siteCode */
  const loadingMap = ref<Record<string, boolean>>({});

  // ====== Getters ======
  /** 获取指定站点的档口列表（内存缓存） */
  function getStallList(siteCode: string): StallItem[] {
    const entry = cache.value[siteCode];
    if (!entry) return [];
    return entry.list;
  }

  /** 指定站点是否正在加载 */
  function isLoading(siteCode: string): boolean {
    return !!loadingMap.value[siteCode];
  }

  /** 指定站点缓存是否有效（30分钟内） */
  function isCacheValid(siteCode: string): boolean {
    const entry = cache.value[siteCode];
    if (!entry) return false;
    return Date.now() - entry.loadedAt < CACHE_EXPIRE * 1000;
  }

  // ====== Storage 缓存读写 ======
  function getStorageKey(siteCode: string): string {
    return `${CACHE_PREFIX}${siteCode}`;
  }

  function loadFromStorage(siteCode: string): StallCacheEntry | null {
    return Storage.get<StallCacheEntry>(getStorageKey(siteCode));
  }

  function saveToStorage(siteCode: string, entry: StallCacheEntry) {
    Storage.set(getStorageKey(siteCode), entry, CACHE_EXPIRE);
  }

  // ====== Actions ======
  /**
   * 获取指定站点的档口列表（带缓存）
   * - 有有效内存缓存：返回缓存，后台静默刷新
   * - 有有效 Storage 缓存：加载到内存，后台静默刷新
   * - 无缓存：直接请求
   */
  async function fetchStallList(siteCode: string, force = false): Promise<StallItem[]> {
    // 非强制刷新时，尝试使用内存缓存
    if (!force && cache.value[siteCode] && isCacheValid(siteCode)) {
      // 后台静默刷新
      silentRefresh(siteCode);
      return cache.value[siteCode].list;
    }

    // 非强制刷新时，尝试使用 Storage 缓存
    if (!force) {
      const stored = loadFromStorage(siteCode);
      if (stored && stored.list.length > 0) {
        cache.value[siteCode] = stored;
        // 后台静默刷新
        silentRefresh(siteCode);
        return stored.list;
      }
    }

    // 直接加载
    return loadFromRemote(siteCode);
  }

  /**
   * 从远程加载档口列表
   */
  async function loadFromRemote(siteCode: string): Promise<StallItem[]> {
    // 防止重复请求
    if (loadingMap.value[siteCode]) {
      await new Promise<void>((resolve) => {
        const timer = setInterval(() => {
          if (!loadingMap.value[siteCode]) {
            clearInterval(timer);
            resolve();
          }
        }, 100);
      });
      return cache.value[siteCode]?.list || [];
    }

    loadingMap.value[siteCode] = true;
    try {
      const res: any[] = await getAllShop(siteCode);
      const list: StallItem[] = (Array.isArray(res) ? res : []).map((item: any) => ({
        id: Number(item.id || item.sellerId || item.seller_id || 0),
        shopName: item.shopName || item.shop_name || '',
      })).filter(s => s.id && s.shopName);

      const entry: StallCacheEntry = { list, loadedAt: Date.now() };
      cache.value[siteCode] = entry;
      saveToStorage(siteCode, entry);

      return list;
    }
    catch (error) {
      console.error(`[StallListStore] 加载站点 ${siteCode} 档口列表失败:`, error);
      // 异常时返回已有缓存
      return cache.value[siteCode]?.list || [];
    }
    finally {
      loadingMap.value[siteCode] = false;
    }
  }

  /**
   * 静默刷新（后台更新，不阻塞调用方）
   */
  function silentRefresh(siteCode: string) {
    if (!isCacheValid(siteCode) && !loadingMap.value[siteCode]) {
      loadFromRemote(siteCode).catch(() => {});
    }
  }

  /**
   * 清空指定站点或全部缓存
   */
  function clearCache(siteCode?: string) {
    if (siteCode) {
      delete cache.value[siteCode];
      delete loadingMap.value[siteCode];
      Storage.remove(getStorageKey(siteCode));
    }
    else {
      // 清空全部
      const keys = Object.keys(cache.value);
      keys.forEach((key) => {
        Storage.remove(getStorageKey(key));
      });
      cache.value = {};
      loadingMap.value = {};
    }
  }

  return {
    cache,
    loadingMap,
    getStallList,
    isLoading,
    isCacheValid,
    fetchStallList,
    loadFromRemote,
    clearCache,
  };
});
